import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { testFirebaseConnection, initializeReportTemplates, createFirestoreIndexes } from '../../utils/firebase-setup';

const FirebaseDebugPanel = ({ onTemplatesCreated }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);

  const runFirebaseTest = async () => {
    if (!currentCompany?.id) {
      toast.error('No company selected');
      return;
    }

    setTesting(true);
    setTestResults(null);

    try {
      // Test basic Firebase connection
      const connectionTest = await testFirebaseConnection(currentCompany.id);
      
      if (connectionTest.success) {
        // Try to initialize templates
        const initResult = await initializeReportTemplates(currentCompany.id, user?.uid);
        
        setTestResults({
          connection: connectionTest,
          initialization: initResult
        });

        if (initResult.success) {
          toast.success('Firebase setup completed successfully!');
          if (onTemplatesCreated) {
            onTemplatesCreated();
          }
        } else {
          toast.error('Failed to initialize templates: ' + initResult.error);
        }
      } else {
        setTestResults({
          connection: connectionTest,
          initialization: { success: false, error: 'Connection failed' }
        });
        toast.error('Firebase connection failed: ' + connectionTest.error);
      }
    } catch (error) {
      console.error('Debug test failed:', error);
      toast.error('Debug test failed: ' + error.message);
      setTestResults({
        connection: { success: false, error: error.message },
        initialization: { success: false, error: 'Test failed' }
      });
    } finally {
      setTesting(false);
    }
  };

  const showIndexInstructions = () => {
    createFirestoreIndexes();
    toast.success('Index instructions logged to console');
  };

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
      <div className="flex items-center mb-3">
        <WrenchScrewdriverIcon className="w-5 h-5 text-yellow-600 mr-2" />
        <h4 className="font-medium text-yellow-800">
          Firebase Debug Panel
        </h4>
      </div>
      
      <p className="text-sm text-yellow-700 mb-4">
        If you're seeing template loading errors, use these tools to diagnose and fix Firebase issues.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={runFirebaseTest}
          disabled={testing}
          className="btn btn-sm bg-yellow-600 hover:bg-yellow-700 text-white"
        >
          {testing ? 'Testing...' : 'Test & Fix Firebase'}
        </button>
        
        <button
          onClick={showIndexInstructions}
          className="btn btn-sm btn-secondary"
        >
          <InformationCircleIcon className="w-4 h-4 mr-1" />
          Index Instructions
        </button>
      </div>

      {testResults && (
        <div className="space-y-2">
          <div className="flex items-center text-sm">
            {testResults.connection.success ? (
              <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
            ) : (
              <XCircleIcon className="w-4 h-4 text-red-600 mr-2" />
            )}
            <span className={testResults.connection.success ? 'text-green-700' : 'text-red-700'}>
              Firebase Connection: {testResults.connection.success ? t('common.success') : 'Failed'}
            </span>
          </div>
          
          <div className="flex items-center text-sm">
            {testResults.initialization.success ? (
              <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
            ) : (
              <XCircleIcon className="w-4 h-4 text-red-600 mr-2" />
            )}
            <span className={testResults.initialization.success ? 'text-green-700' : 'text-red-700'}>
              Template Initialization: {testResults.initialization.success ? t('common.success') : 'Failed'}
            </span>
          </div>

          {!testResults.connection.success && (
            <div className="text-xs text-red-600 mt-2">
              Error: {testResults.connection.error}
            </div>
          )}
          
          {!testResults.initialization.success && testResults.connection.success && (
            <div className="text-xs text-red-600 mt-2">
              Error: {testResults.initialization.error}
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-yellow-600 mt-3">
        <strong>Common Issues:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>Missing Firestore indexes (click "Index Instructions")</li>
          <li>Firebase permissions not configured</li>
          <li>Network connectivity issues</li>
          <li>Company ID not set properly</li>
        </ul>
      </div>
    </div>
  );
};

export default FirebaseDebugPanel;